/* eslint-disable no-param-reassign -- `updates` is an intentional out-parameter for all setDraft* helpers */
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/**
 * Coerces an input-like value into a string for API updates.
 * - Only sets the field when it exists on the draft (touched semantics).
 * - Converts null/undefined to empty string.
 *
 * @param {{draft: Object, updates: Object, field: string}} params
 */
export const setDraftStringField = ({ draft, field, updates }) => {
    if (!hasOwn(draft, field)) return;
    updates[field] = String(draft[field] ?? '');
};

/**
 * Coerces an input-like value into an integer for API updates.
 * - Only sets the field when it exists on the draft (touched semantics).
 * - Uses trimmed string parsing, allowing the blank string to explicitly clear.
 * - Uses Number.parseInt to match browser input behavior (e.g. "12.3" -> 12).
 *
 * @param {{draft: Object, updates: Object, field: string}} params
 */
export const setDraftIntegerField = ({ draft, field, updates }) => {
    if (!hasOwn(draft, field)) return;
    const raw = String(draft[field] ?? '').trim();
    if (raw === '') {
        updates[field] = '';
        return;
    }
    const num = Number.parseInt(raw, 10);
    if (!Number.isNaN(num)) updates[field] = num;
};

/**
 * Coerces an input-like value into a float for API updates.
 * - Only sets the field when it exists on the draft (touched semantics).
 * - Uses trimmed string parsing, allowing the blank string to explicitly clear.
 * - Rounds to 2 decimal places by default (override with precision when needed).
 *
 * @param {{draft: Object, updates: Object, field: string, precision?: number}} params
 */
export const setDraftFloatField = ({ draft, field, precision = 2, updates }) => {
    if (!hasOwn(draft, field)) return;
    const raw = String(draft[field] ?? '').trim();
    if (raw === '') {
        updates[field] = '';
        return;
    }
    const num = Number.parseFloat(raw);
    if (!Number.isNaN(num)) updates[field] = Number(num.toFixed(precision));
};

/**
 * Coerces a draft value into a boolean for API updates.
 * Only sets the field when it exists on the draft (touched semantics).
 *
 * @param {{draft: Object, updates: Object, field: string}} params
 */
export const setDraftBooleanField = ({ draft, field, updates }) => {
    if (!hasOwn(draft, field)) return;
    const raw = draft[field];
    if (raw === true || raw === 'true' || raw === 1 || raw === '1') {
        updates[field] = true;
        return;
    }
    if (raw === false || raw === 'false' || raw === 0 || raw === '0') {
        updates[field] = false;
        return;
    }
    if (raw === '' || raw == null) {
        updates[field] = false;
        return;
    }
    const s = String(raw).trim().toLowerCase();
    if (s === 'yes' || s === 'y') {
        updates[field] = true;
        return;
    }
    if (s === 'no' || s === 'n') {
        updates[field] = false;
        return;
    }
    updates[field] = Boolean(raw);
};
